import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import { ArrowLeft, Heart, HeartHandshake, Eye, Star, Calendar, MapPin, Camera, Flame, Crown, Coins, Lock, Unlock, MessageCircle, Volume2, FileImage, Plus, Zap, X, CircleDollarSign } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";


import sampleCoupleHug from "@/assets/sample-couple-hug.jpg";
import adamPhoto1 from "@/assets/adam-photo-1.jpg";

// No avatar maps or defaults

interface ProfilePageProps {
  boyfriendId: string;
  user: {
    id?: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onBack: () => void;
  onPromptLogin: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: () => void;
  userTokens?: number;
  onUpdateTokens?: (newTokens: number) => void;
  onPurchaseTokens?: (tokens: number, paymentMethod: string) => void;
  onTokensClick?: () => void;
  source?: 'chat' | 'explore' | 'myBoyfriends';
  favorites?: Set<string>;
  onToggleFavorite?: (boyfriendId: string) => void;
}

// Import BoyfriendProfile from API models
import { BoyfriendProfile } from '@/api/models';

export const ProfilePage = ({ 
  boyfriendId, 
  user, 
  onBack, 
  onPromptLogin,
  onNavigateToChat,
  onNavigateToMyBoyfriends,
  onNavigateToProfile,
  onNavigateToSettings,
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  userTokens = 0,
  onUpdateTokens,
  onPurchaseTokens,
  onTokensClick,
  source = 'chat',
  favorites = new Set(),
  onToggleFavorite
}: ProfilePageProps) => {
  const [profile, setProfile] = useState<BoyfriendProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showPrivateContentModal, setShowPrivateContentModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  // Token/purchase flow is centralized in TokenBalanceModal; open it via onTokensClick
  const [generationType, setGenerationType] = useState<'photo'>('photo');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [unlockedPhotos, setUnlockedPhotos] = useState<Set<string>>(new Set());
  const [spicyPhotos, setSpicyPhotos] = useState<Array<{ url: string; unlockKey: string; locked: boolean; isLockable: boolean; priceTokens: number | null; description: string | null }>>([]);
  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  // Local state for tokens in modal - always loaded from API, updates immediately after unlock
  const [modalTokens, setModalTokens] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [referenceImageUrl, setReferenceImageUrl] = useState<string>('');
  const [uploadedUserPhoto, setUploadedUserPhoto] = useState<string>('');
  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Removed hardcoded sample profiles

  // Reference image can default to current profile's avatar when loaded

  useEffect(() => {
    const loadProfile = async () => {
      setIsLoadingProfile(true);
      
      // Try API first by boyfriend ID
      try {
        const data = await apiClient.get(`/boyfriends/${boyfriendId}`);
        if (data) {
          if (data.avatarUrl) {
            setReferenceImageUrl(data.avatarUrl);
          }
          // Normalize interests
          let interests: string[] = [];
          if (Array.isArray(data.interests)) {
            interests = data.interests;
          } else if (typeof data.interests === 'string') {
            try {
              const parsed = JSON.parse(data.interests);
              interests = Array.isArray(parsed) ? parsed : [data.interests];
            } catch {
              interests = [data.interests];
            }
          }
          const parsedAge = typeof data.age === 'number' ? data.age : (typeof data.age === 'string' ? parseInt(data.age) : undefined);

          const apiProfile: BoyfriendProfile = {
            id: data.id ?? boyfriendId,
            name: data.name,
            avatarUrl: data.avatarUrl,
            personality: data.personality,
            age: typeof parsedAge === 'number' && !isNaN(parsedAge) ? parsedAge.toString() : undefined,
            ethnicity: data.ethnicity || '',
            bodyType: data.bodyType || '',
            hairstyle: data.hairstyle || '',
            clothingStyle: data.clothingStyle || '',
            interests: interests,
            tattoos: data.tattoos || '',
            communicationStyle: data.communicationStyle || '',
            isPremium: data.isPremium || false,
            location: data.location,
            occupation: data.occupation,
            bio: data.bio,
            rating: data.rating || 0,
            popularity: data.popularity || 0,
            style: data.style || (data as any).art_style || undefined,
            createdAt: data.createdAt || new Date().toISOString(),
            photosUrl: Array.isArray(data.photosUrl) ? data.photosUrl : [],
            spicyPhotosUrl: Array.isArray(data.spicyPhotosUrl) ? data.spicyPhotosUrl : [],
            stats: typeof data.stats === 'object' ? data.stats : undefined,
          };

          setProfile(apiProfile);
          setIsLoadingProfile(false);
          return;
        }
      } catch (error) {
        console.error('Error loading boyfriend via API:', error);
      }


      setIsLoadingProfile(false);
    };
    
    loadProfile();
  }, [boyfriendId, user?.id]);

  // Carousel effect to track current slide
  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCount(carouselApi.scrollSnapList().length);
    setCurrent(carouselApi.selectedScrollSnap() + 1);

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap() + 1);
    });
  }, [carouselApi]);

  // Function to scroll to specific slide
  const scrollTo = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);


  // Load tokens from API when any modal opens - always fetch fresh tokens to ensure accuracy
  useEffect(() => {
    if ((showPrivateContentModal || showGenerateModal) && user?.id) {
      const loadTokens = async () => {
        try {
          // Always fetch fresh tokens from API when modal opens to ensure accuracy
          // Use /users/me endpoint which returns User object with currentTokens
          const userData = await apiClient.get('/users/me');
          const freshTokens = userData?.currentTokens || 0;
          setModalTokens(freshTokens);
          // Also update parent if callback exists
          if (onUpdateTokens) {
            onUpdateTokens(freshTokens);
          }
        } catch (error) {
          console.error('Error fetching user tokens:', error);
          setModalTokens(0);
        }
      };
      loadTokens();
    } else if (showPrivateContentModal || showGenerateModal) {
      // No user logged in
      setModalTokens(0);
    }
  }, [showPrivateContentModal, showGenerateModal, user?.id]);

  // Load spicy photos with unlock status
  useEffect(() => {
    const loadSpicyPhotos = async () => {
      if (!profile || !profile.id) {
        return;
      }
      
      try {
        const photos = await apiClient.getSpicyPhotos(profile.id);
        if (Array.isArray(photos)) {
          setSpicyPhotos(photos);
        }
      } catch (error) {
        console.error('Error loading spicy photos:', error);
        toast({
          title: t('boyfriend_profile.load_error') || 'Failed to load spicy photos',
          variant: 'destructive',
        });
        // Fallback to empty array
        setSpicyPhotos([]);
      }
    };
    
    loadSpicyPhotos();
  }, [boyfriendId, profile?.id]); // Removed toast and t from dependencies to prevent infinite loop

  const handleToggleFavorite = async () => {
    if (!profile || !onToggleFavorite) {
      console.log('Missing profile or onToggleFavorite function');
      return;
    }
    console.log('Toggling favorite for profile:', profile.id, profile.name);
    await onToggleFavorite(profile.id);
  };

  const handleUnlockSpicyPhoto = async (unlockKey: string, priceTokens: number | null) => {
    if (!user || !profile) {
      onPromptLogin();
      return;
    }
    
    if (priceTokens == null || priceTokens <= 0) {
      toast({
        title: t('boyfriend_profile.unlock_error') || 'Invalid price for this photo',
        variant: 'destructive',
      });
      return;
    }
    
    const requiredTokens = priceTokens;
    if (modalTokens < requiredTokens) {
      onTokensClick?.();
      return;
    }
    
    try {
      const result = await apiClient.unlockImage(unlockKey);
      
      if (result.success) {
        // Reload spicy photos to get updated unlock status
        const photos = await apiClient.getSpicyPhotos(profile.id);
        if (Array.isArray(photos)) {
          setSpicyPhotos(photos);
        }
        
        // Update tokens from backend response (backend returns currentTokens after deduction)
        if (result.currentTokens !== undefined) {
          // Update modal tokens immediately for UI refresh
          setModalTokens(result.currentTokens);
          // Also update parent component
          if (onUpdateTokens) {
            onUpdateTokens(result.currentTokens);
          }
        }
        
        toast({
          title: t('boyfriend_profile.unlock_success'),
          description: t('boyfriend_profile.unlock_success_desc'),
          variant: 'default',
        });
      } else {
        toast({
          title: result.error || (t('boyfriend_profile.unlock_error') || 'Failed to unlock photo'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error unlocking photo:', error);
      toast({
        title: error?.message || (t('boyfriend_profile.unlock_error') || 'Failed to unlock photo'),
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = async () => {
    if (!user) {
      onPromptLogin();
      return;
    }
    
    const cost = 0; // Set to 0 for testing
    if (modalTokens < cost) {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('resumeGenerationAfterTokens', '1');
        }
      } catch {}
      onTokensClick?.();
      return;
    }

    if (!generatePrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please describe your vision before generating.",
        variant: "destructive"
      });
      return;
    }
    
    // Open generation dialog and start generating
    setShowGenerationDialog(true);
    setGeneratedImageUrl(null);
    setIsGenerating(true);
    setShowGenerateModal(false); // Close the prompt modal
    
    try {
      const res = await apiClient.generateImage({ userPrompt: generatePrompt, boyfriendId, userPhotoBase64: uploadedUserPhoto });
      const imageUrl = res?.imageUrl || null;
      if (!imageUrl) {
        toast({
          title: "No image returned",
          description: "The server did not return an image URL.",
          variant: "destructive"
        });
        setIsGenerating(false);
        setShowGenerationDialog(false);
        return;
      }
      setGeneratedImageUrl(imageUrl);
      setShowGenerationDialog(true);
      // Token balance is updated on server; optionally refresh elsewhere
      setIsGenerating(false);
      setGeneratePrompt('');
      toast({ title: "Image Generated!", description: "Your custom image has been created." });
    } catch (error: any) {
      if (error?.status === 400) {
        setIsGenerating(false);
        setShowGenerationDialog(false);
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('resumeGenerationAfterTokens', '1');
          }
        } catch {}
        onTokensClick?.();
        return;
      }
      console.error('Error generating image:', error);
      toast({
        title: "Generation Failed",
        description: error?.message || (error instanceof Error ? error.message : "An unexpected error occurred."),
        variant: "destructive"
      });
      setIsGenerating(false);
      setShowGenerationDialog(false);
    }
  };

  // When token modal closes and we need to resume generation, reopen the generate modal
  useEffect(() => {
    const handler = () => {
      try {
        const flag = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('resumeGenerationAfterTokens') === '1';
        if (flag) {
          sessionStorage.removeItem('resumeGenerationAfterTokens');
          setShowGenerateModal(true);
        }
      } catch {}
    };
    window.addEventListener('resume-generation', handler);
    return () => window.removeEventListener('resume-generation', handler);
  }, []);

  const handlePlayVoice = async () => {
    return;
  };

  // Show loading state
  if (isLoadingProfile) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-dreamy">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('boyfriend_profile.loading')}</p>
        </div>
      </div>
    );
  }

  // Show profile not found only after loading is complete
  if (!profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-dreamy">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('boyfriend_profile.not_found')}</h2>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('boyfriend_profile.go_back')}
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16 overflow-x-hidden">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 px-4 py-3 shrink-0">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {source === 'explore' ? t('boyfriend_profile.back_to_explore') : source === 'myBoyfriends' ? t('boyfriend_profile.back_to_my_boyfriends') : t('boyfriend_profile.back_to_chat')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="overflow-y-auto overflow-x-hidden">
        {/* Profile Header Carousel - Full Width */}
        <div className="relative">
          <Carousel className="w-full" setApi={setCarouselApi}>
            <CarouselContent>
              {(profile.photosUrl || []).map((photo, index) => (
                <CarouselItem key={index}>
                  <div className="relative aspect-square bg-gradient-to-br from-accent/50 to-background">
                    <img
                      src={photo}
                      alt={`${profile.name} photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          
          {/* Favorites Button - Floating Top Right */}
          <div className="absolute top-3 right-6 z-10">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleToggleFavorite}
              className={`h-10 w-10 border border-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-110 ${
                favorites.has(profile.id) 
                  ? 'text-red-500 hover:text-red-600 bg-red-500/20 hover:bg-red-500/30 ring-2 ring-red-500/50 border-red-500/50 shadow-lg shadow-red-500/25' 
                  : 'text-white hover:text-red-500 bg-white/20 hover:bg-red-500/20 hover:border-red-500/50 hover:shadow-lg hover:shadow-red-500/25'
              }`}
            >
              <Heart 
                className={`h-5 w-5 transition-all duration-300 ${
                  favorites.has(profile.id) 
                    ? 'fill-current scale-110 drop-shadow-lg' 
                    : 'fill-none hover:fill-current/30'
                }`}
              />
            </Button>
          </div>
          
          {/* Name and Age - Floating Bottom Left */}
          <div className="absolute bottom-12 left-8 right-8 text-white z-10">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-3xl font-bold">{profile.name}, {profile.age}</h1>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            {profile.location && (
            <p className="text-sm opacity-90 flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {profile.location}
            </p>
            )}
            {profile.occupation && (
            <p className="text-xs opacity-75 flex items-center gap-1 mt-1">
              <Calendar className="h-4 w-4" />
              {profile.occupation}
            </p>
            )}
          </div>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
            {(profile.photosUrl || []).map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  current === index + 1 
                    ? 'bg-white scale-125' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="max-w-6xl p-6">

        {/* Profile Info */}
        <div className="space-y-6 mb-8 pr-6 text-left">{/* Added text-left class */}
            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              {/* Top row: Chat Now and My Private Content */}
              <div className="flex gap-3 items-center">
                <Button
                  variant="romantic"
                  onClick={onNavigateToChat}
                  className="hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-500 transition-all duration-300 hover:scale-125 hover:shadow-lg hover:shadow-pink-500/50"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t('boyfriend_profile.chat_now')}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowPrivateContentModal(true)}
                  className="bg-gradient-to-r from-red-500/20 to-pink-500/20 hover:from-red-500/40 hover:to-pink-500/40 border-red-500/40 text-red-600 hover:text-red-700 font-semibold shadow-lg hover:shadow-red-500/25 transition-all duration-300 hover:scale-105"
                >
                  🔥 {t('boyfriend_profile.my_private_content')}
                </Button>
              </div>
              
              {/* Hear My Voice and Generate New Photo buttons */}
              <div className="flex gap-3 items-center">
                <Button 
                  onClick={() => setShowGenerateModal(true)}
                  variant="outline"
                  className="bg-gradient-to-r from-blue-900 to-purple-600 hover:from-blue-800 hover:to-purple-500 border-blue-500/20 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  {t('boyfriend_profile.generate_photo')}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handlePlayVoice}
                  disabled={isPlayingVoice}
                  className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20 border-blue-500/20"
                >
                  <Volume2 className="h-4 w-4 mr-2" />
                  {isPlayingVoice ? t('boyfriend_profile.playing') : t('boyfriend_profile.my_voice')}
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">{t('boyfriend_profile.about_me')}</h3>
              {profile.bio && (
                <p className="text-muted-foreground leading-relaxed text-sm">{profile.bio}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {profile.personality && (
                <Badge variant="secondary" className="capitalize text-sm px-2 py-1">
                  {profile.personality}
                </Badge>
              )}
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">{t('boyfriend_profile.interests')}</h3>
              <div className="flex flex-wrap gap-2">
                {(profile.interests || []).map((interest, index) => (
                  <Badge key={index} variant="outline">{interest}</Badge>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>

      {/* Private Content Modal */}
      <Dialog open={showPrivateContentModal} onOpenChange={setShowPrivateContentModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <button
            onClick={() => setShowPrivateContentModal(false)}
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-red-500" />
              <DialogTitle className="text-base sm:text-lg">{profile.name} - {t('boyfriend_profile.my_private_content')}</DialogTitle>
            </div>
            
            {/* Token balance and add button on same line */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20">
                  <Coins className="h-4 w-4 text-amber-500" />
                  <span className="font-semibold">{modalTokens}</span>
                  <span className="text-muted-foreground text-sm">{t('boyfriend_profile.tokens')}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onTokensClick?.()}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-amber-400 hover:from-amber-600 hover:to-yellow-600 hover:shadow-lg transition-all duration-200 hover:scale-105 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('boyfriend_profile.add_tokens')}
                 </Button>
             </div>
          </DialogHeader>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {spicyPhotos.map((photo, index) => {
              const isLocked = photo.locked;
              const isLockable = photo.isLockable;
              
              // unlockKey must always be present for spicy photos
              return (
                <div key={photo.unlockKey} className="group relative">
                  <div className="aspect-square overflow-hidden rounded-lg border-2 border-red-500/20">
                    <img
                      src={photo.url}
                      alt={photo.description || `${t('boyfriend_profile.private_photo')} ${index + 1}`}
                      className={`w-full h-full object-cover transition-all duration-300 ${
                        !isLocked ? 'cursor-pointer hover:scale-105' : ''
                      }`}
                      onClick={!isLocked ? () => setFullScreenPhoto(photo.url) : undefined}
                    />
                    {isLocked && isLockable && photo.priceTokens != null && photo.priceTokens > 0 && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-red-500/20 to-transparent flex items-center justify-center">
                        <Button
                          variant="romantic"
                          size="sm"
                          onClick={() => handleUnlockSpicyPhoto(photo.unlockKey, photo.priceTokens)}
                          className="shadow-lg bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 border-0 text-white transform hover:scale-105 transition-all duration-300 text-xs sm:text-sm px-2 sm:px-3"
                        >
                          <Lock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span className="hidden sm:inline">{t('boyfriend_profile.unlock')} ({photo.priceTokens} {t('boyfriend_profile.unlock_tokens')})</span>
                          <span className="sm:hidden flex items-center gap-1"><CircleDollarSign className="h-3 w-3 text-amber-500" />{photo.priceTokens}</span>
                        </Button>
                      </div>
                    )}
                    {!isLocked && (
                      <div className="absolute top-2 right-2">
                        <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                          <span>✓</span>
                          {t('boyfriend_profile.unlocked')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Content Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent 
          className="w-screen h-screen max-w-none m-0 p-0 rounded-none overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="relative bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 p-6 border-b border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10" />
            
            {/* Back button */}
            <button
              onClick={() => setShowGenerateModal(false)}
              className="absolute top-4 left-4 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            
            <div className="relative z-10 mt-8">
              <DialogHeader className="space-y-3">
                <DialogTitle className="text-2xl md:text-3xl font-bold bg-gradient-romantic bg-clip-text text-transparent text-center">
                  {t('boyfriend_profile.generate_custom_content')}
                </DialogTitle>
                <DialogDescription className="text-center text-muted-foreground text-base md:text-lg">
                  {t('boyfriend_profile.create_personalized')} {profile.name}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Sample Video Preview */}
            <div className="space-y-4">
              <Card className="overflow-hidden border-2 border-primary/20">
                <div className="relative">
                  <img 
                    src={sampleCoupleHug} 
                    alt="Sample romantic couple"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h4 className="font-bold text-lg mb-1">{t('boyfriend_profile.romantic_moments')}</h4>
                    <p className="text-sm opacity-90">{t('boyfriend_profile.create_intimate')} {profile.name}</p>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                      {t('boyfriend_profile.sample')}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Prompt Input */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t('boyfriend_profile.describe_vision')}</h3>
              <Textarea
                placeholder={`Me and ${profile.name} hugging tightly...`}
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={4}
                autoFocus={false}
                className="border-2 border-primary/20 focus:border-primary/40 resize-none placeholder:text-muted-foreground"
              />
            </div>

            {/* Photo Upload */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t('boyfriend_profile.upload_photo')}</h3>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      console.log('Selected file:', file);
                      // Convert file to base64 data URL
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const base64String = reader.result as string;
                        setUploadedUserPhoto(base64String);
                        console.log('User photo uploaded and converted to base64');
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
                {uploadedUserPhoto && (
                  <div className="relative w-32 h-32 border-2 border-primary/20 rounded-lg overflow-hidden">
                    <img 
                      src={uploadedUserPhoto} 
                      alt="Uploaded preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setUploadedUserPhoto('')}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer with Actions */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-primary/20">
              <div className="flex items-center gap-2 order-2 md:order-1">
                <Coins className="h-5 w-5 text-amber-500" />
                <span className="font-semibold">{modalTokens} {t('boyfriend_profile.tokens_available')}</span>
              </div>
              <div className="flex gap-3 order-1 md:order-2 w-full md:w-auto">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || !generatePrompt.trim()}
                  variant="romantic"
                  className="flex-1 md:flex-none bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 hover:shadow-lg transition-all duration-300"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('boyfriend_profile.generating')}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      {t('boyfriend_profile.generate')} ({generationType === 'photo' ? 20 : 50} {t('boyfriend_profile.tokens')})
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Token purchase flow removed here; handled by global TokenBalanceModal via onTokensClick */}

      {/* Generation Progress Dialog */}
      <Dialog open={showGenerationDialog} onOpenChange={setShowGenerationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              {isGenerating ? t('boyfriend_profile.generating_image') : t('boyfriend_profile.image_generated')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-6">
            {isGenerating ? (
              <div className="flex flex-col items-center justify-center space-y-6">
                <div className="w-full max-w-md space-y-3">
                  <Progress value={undefined} className="h-2" />
                  <p className="text-center text-muted-foreground">
                    {t('boyfriend_profile.creating_custom')}
                  </p>
                </div>
                <div className="animate-pulse text-sm text-muted-foreground">
                  {t('boyfriend_profile.take_moments')}
                </div>
              </div>
            ) : generatedImageUrl ? (
              <div className="space-y-4">
                <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-muted">
                  <img 
                    src={generatedImageUrl} 
                    alt="Generated image" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowGenerationDialog(false);
                      setGeneratedImageUrl(null);
                    }}
                    className="h-10"
                  >
                    {t('boyfriend_profile.close')}
                  </Button>
                  <Button
                    onClick={() => {
                      // Download the image
                      if (generatedImageUrl) {
                        const link = document.createElement('a');
                        link.href = generatedImageUrl;
                        link.download = `generated-${profile?.name || 'image'}-${Date.now()}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 h-10"
                  >
                    {t('boyfriend_profile.save_image')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen Photo Modal */}
      {fullScreenPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            console.log('Full screen background clicked');
            e.stopPropagation();
            setFullScreenPhoto(null);
          }}
        >
          <div className="relative max-w-full max-h-full">
            <button
              type="button"
              onClick={(e) => {
                console.log('X button clicked');
                e.preventDefault();
                e.stopPropagation();
                console.log('About to close full screen photo');
                setFullScreenPhoto(null);
                console.log('Full screen photo closed, opening private content modal');
                setShowPrivateContentModal(true);
              }}
              className="absolute top-4 right-4 bg-black/70 text-white rounded-full p-3 hover:bg-black/90 transition-colors z-[99999] border-2 border-white/20 hover:border-white/40 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <X className="h-6 w-6 pointer-events-none" />
            </button>
            <img
              src={fullScreenPhoto}
              alt="Full screen private photo"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};